import { c as _c } from "react/compiler-runtime";
import { postOrgPlaybooksEditSessionsByEditSessionIdCommitMutation } from '@onefootprint/fetch-dashboard';
import type { PlaybookSettings } from '@onefootprint/request-types/dashboard';
import { Button, Dialog, Form, useToast } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useEdit from '../../hooks/use-edit';
import useHiddenNodeErrors from '../../hooks/use-hidden-node-errors';
import useStore from '../../hooks/use-store';
export type CreateDialogProps = {
  onDone: (playbookId: string) => void;
};
const CreateDialog = ({
  onDone
}: CreateDialogProps) => {
  const {
    t
  } = useTranslation('playbook-builder', {
    keyPrefix: 'header.createDialog'
  });
  const [isOpen, setIsOpen] = useState(false);
  const editMutation = useEdit();
  const commitMutation = useMutation(postOrgPlaybooksEditSessionsByEditSessionIdCommitMutation());
  const store = useStore();
  const {
    toast
  } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {
    data: {
      settings
    },
    visibleBanners
  } = store;
  const {
    hasHiddenErrors,
    reveal: revealHiddenErrors
  } = useHiddenNodeErrors();
  const handleOpen = () => {
    if (hasHiddenErrors) return revealHiddenErrors();
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const handleSubmit = async (data: FormData) => {
    if (!store.data.sessionId) return;
    try {
      setIsSubmitting(true);
      await editMutation.mutateAsync({
        kind: 'update_settings',
        name: data.name
      });
      await publish(store.data.sessionId);
      setIsOpen(false);
      if (store.data.playbookId) {
        onDone(store.data.playbookId);
      }
    } catch (_) {} finally {
      setIsSubmitting(false);
    }
  };
  const publish = async (sessionId: string) => {
    try {
      await commitMutation.mutateAsync({
        path: {
          editSessionId: sessionId
        },
        body: {
          action: 'publish',
          skipValidation: false
        }
      });
      toast({
        title: t($_1 => $_1.feedback.saveSuccess.title),
        description: t($_2 => $_2.feedback.saveSuccess.description)
      });
    } catch (__0) {
      toast({
        variant: 'error',
        title: t($ => $.feedback.saveError.title),
        description: t($_0 => $_0.feedback.saveError.description)
      });
    }
  };
  return <>
      <Button onClick={handleOpen} size="compact" disabled={!hasHiddenErrors && visibleBanners.some(b => b.type === 'error')}>
        {t($_3 => $_3.trigger)}
      </Button>
      <Dialog title={t($_4 => $_4.title)} open={isOpen} onClose={handleClose} size="compact" primaryButton={{
      form: 'playbook-name-form',
      label: t($_5 => $_5.cta),
      loading: isSubmitting,
      type: 'submit'
    }} secondaryButton={{
      disabled: isSubmitting,
      label: t($_6 => $_6.cancel),
      onClick: handleClose
    }}>
        <NameForm onSubmit={handleSubmit} playbookSettings={settings} />
      </Dialog>
    </>;
};
type FormData = {
  name: string;
};
type NameFormProps = {
  onSubmit: (data: FormData) => void;
  playbookSettings?: PlaybookSettings;
};
const NameForm = t0 => {
  const $ = _c(24);
  const {
    onSubmit,
    playbookSettings
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "header.createDialog"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("playbook-builder", t1);
  const t2 = playbookSettings?.name || "";
  let t3;
  if ($[1] !== t2) {
    t3 = {
      defaultValues: {
        name: t2
      }
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const formMethods = useForm(t3);
  const {
    handleSubmit
  } = formMethods;
  let t4;
  if ($[3] !== handleSubmit || $[4] !== onSubmit) {
    t4 = handleSubmit(onSubmit);
    $[3] = handleSubmit;
    $[4] = onSubmit;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t(_temp);
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t(_temp2);
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t(_temp3);
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = {
      required: t7
    };
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t5 || $[15] !== t6 || $[16] !== t8) {
    t9 = <Form.Input name="name" size="compact" label={t5} placeholder={t6} rules={t8} />;
    $[14] = t5;
    $[15] = t6;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t4 || $[19] !== t9) {
    t10 = <form className="flex flex-col gap-6" id="playbook-name-form" onSubmit={t4}>{t9}</form>;
    $[18] = t4;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== formMethods || $[22] !== t10) {
    t11 = <FormProvider {...formMethods}>{t10}</FormProvider>;
    $[21] = formMethods;
    $[22] = t10;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
};
export default CreateDialog;
function _temp($) {
  return $.name.label;
}
function _temp2($_0) {
  return $_0.name.placeholder;
}
function _temp3($_1) {
  return $_1.name.errors.required;
}